import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentTextIcon, CodeBracketIcon } from '@heroicons/react/24/outline'
import { colorMap } from '../../../Common'

const ModalServico = ({ states, setters, actionCreate, actionEdit, color = 'blue' }) => {
    const colorClasses = colorMap[color] || colorMap.blue

    const [nome, setNome] = useState('')
    const [descricao, setDescricao] = useState('')
    const [municipal, setMunicipal] = useState(false)
    const [codigoServico, setCodigoServico] = useState('')

    const [errors, setErrors] = useState({})

    const validate = () => {
        const newErrors = {}

        if (!nome.trim()) newErrors.nome = 'Nome é obrigatório'
        if (!descricao.trim()) newErrors.descricao = 'Descrição é obrigatória'
        if (!codigoServico.trim()) newErrors.codigoServico = 'Código do serviço é obrigatório'

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) {
            return
        }

        const dataToSend = {
            nome: nome,
            descricao: descricao,
            municipal: municipal.toString(),
            codigo_servico: codigoServico
        }

        if (states.isEdit) {
            actionEdit(states.Servico.id, dataToSend)
        } else {
            actionCreate(dataToSend)
        }
    }

    useEffect(() => {
        if (!states.modalServico) {
            setNome('')
            setDescricao('')
            setMunicipal(false)
            setCodigoServico('')
            setErrors({})
        } else {
            if (states.isEdit && states.Servico) {
                const servico = states.Servico
                setNome(servico.nome || '')
                setDescricao(servico.descricao || '')
                setMunicipal(servico.municipal === 'true' || servico.municipal === true)
                setCodigoServico(servico.codigo_servico || '')
            }
        }
    }, [states.modalServico, states.isEdit, states.Servico])

    return (
        <Transition.Root show={states.modalServico} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalServico(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 transition-colors"
                                        onClick={() => setters.setModalServico(false)}
                                    >
                                        <span className="sr-only">Fechar</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className={`border-b ${states.isEdit ? colorClasses.border : 'border-gray-200'} pb-4`}>
                                    <div className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg ${states.isEdit ? colorClasses.containerBg : 'bg-gray-50'} mb-3`}>
                                        <span className={`text-sm font-semibold ${states.isEdit ? colorClasses.textGradient : 'text-gray-700'}`}>
                                            {states.isEdit ? '✏️ Modo Edição' : '➕ Novo Serviço'}
                                        </span>
                                    </div>
                                    <h3 className={`text-2xl font-bold leading-7 text-center ${states.isEdit ? colorClasses.textGradient : 'text-gray-900'}`}>
                                        {states.isEdit ? 'Editar Serviço' : 'Novo Serviço'}
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500 text-center">
                                        {states.isEdit ? 'Atualize as informações do serviço abaixo' : 'Preencha os dados para criar um novo serviço'}
                                    </p>
                                </div>

                                <div className="mt-6 space-y-8">
                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <DocumentTextIcon className="h-5 w-5 text-brand-cyan" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações Básicas</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div className="sm:col-span-2">
                                                <label htmlFor="nome" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Nome do Serviço <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="nome"
                                                    name="nome"
                                                    value={nome}
                                                    onChange={(e) => {
                                                        setNome(e.target.value)
                                                        if (errors.nome) setErrors({ ...errors, nome: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.nome ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-cyan'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Digite o nome do serviço"
                                                />
                                                {errors.nome && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.nome}</p>
                                                )}
                                            </div>

                                            <div className="sm:col-span-2">
                                                <label htmlFor="descricao" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Descrição <span className="text-red-500">*</span>
                                                </label>
                                                <textarea
                                                    id="descricao"
                                                    name="descricao"
                                                    rows={3}
                                                    value={descricao}
                                                    onChange={(e) => {
                                                        setDescricao(e.target.value)
                                                        if (errors.descricao) setErrors({ ...errors, descricao: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.descricao ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-cyan'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Digite a descrição do serviço"
                                                />
                                                {errors.descricao && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <CodeBracketIcon className="h-5 w-5 text-brand-cyan" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações Adicionais</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="codigoServico" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Código do Serviço <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="codigoServico"
                                                    name="codigoServico"
                                                    value={codigoServico}
                                                    onChange={(e) => {
                                                        setCodigoServico(e.target.value)
                                                        if (errors.codigoServico) setErrors({ ...errors, codigoServico: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.codigoServico ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-cyan'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Digite o código do serviço"
                                                />
                                                {errors.codigoServico && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.codigoServico}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label htmlFor="municipal" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Municipal <span className="text-red-500">*</span>
                                                </label>
                                                <div className="mt-2 flex items-center space-x-6">
                                                    <label className="inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="municipal"
                                                            checked={municipal === true}
                                                            onChange={() => setMunicipal(true)}
                                                            className="h-4 w-4 text-brand-cyan focus:ring-brand-cyan border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">Sim</span>
                                                    </label>
                                                    <label className="inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="municipal"
                                                            checked={municipal === false}
                                                            onChange={() => setMunicipal(false)}
                                                            className="h-4 w-4 text-brand-cyan focus:ring-brand-cyan border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">Não</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 flex items-center justify-end gap-x-4 border-t border-gray-200 pt-6">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalServico(false)}
                                        className="rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm ${colorClasses.headerButtonBg} ${colorClasses.headerButtonHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${colorClasses.focusRingOffset} transition-all`}
                                    >
                                        {states.isEdit ? 'Salvar Alterações' : 'Criar Serviço'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalServico

